import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AthleteProvider } from './contexts/AthleteContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import AdminAthletes from './pages/AdminAthletes';
import StaffAthletes from './pages/StaffAthletes';
import VisualRoster from './pages/VisualRoster';
import PlayerProfile from './pages/PlayerProfile';
import StaffTraining from './pages/StaffTraining';
import PlayerTraining from './pages/PlayerTraining';
import StaffMatches from './pages/StaffMatches';
import MatchesPage from './pages/MatchesPage';
import MatchDetailPage from './pages/MatchDetailPage';
import MatchPerformancePage from './pages/MatchPerformancePage';
import ClubsAdminPage from './pages/ClubsAdminPage';
import PlayerPerformance from './pages/PlayerPerformance';
import StaffMedical from './pages/StaffMedical';
import PlayerMedical from './pages/PlayerMedical';
import StaffAcademic from './pages/StaffAcademic';
import PlayerAcademic from './pages/PlayerAcademic';
import StaffFinance from './pages/StaffFinance';
import PlayerFinance from './pages/PlayerFinance';
import FinanceReports from './pages/FinanceReports';
import StaffDocuments from './pages/StaffDocuments';
import PlayerDocuments from './pages/PlayerDocuments';
import StaffAnnouncements from './pages/StaffAnnouncements';
import PlayerAnnouncements from './pages/PlayerAnnouncements';
import Athlete360Dashboard from './pages/Athlete360Dashboard';
import My360Redirect from './pages/My360Redirect';
import CompareAthletes from './pages/CompareAthletes';
import AdminUserManagement from './pages/AdminUserManagement';
import DecisionEnginePage from './pages/DecisionEnginePage';
import StaffAssessments from './pages/StaffAssessments';
import PlayerAssessments from './pages/PlayerAssessments';
import BulkAssessmentsList from './pages/BulkAssessmentsList';
import BulkAssessmentDetail from './pages/BulkAssessmentDetail';
import FitnessDashboard from './pages/FitnessDashboard';
import StandardsReview from './pages/StandardsReview';
import StaffWellness from './pages/StaffWellness';
import PlayerWellness from './pages/PlayerWellness';
import AthleteMedia from './pages/AthleteMedia';
import PlayerMedia from './pages/PlayerMedia';
import AccessControlPanel from './pages/AccessControlPanel';
import PDPPage from './pages/PDPPage';
import CareerPathwayPage from './pages/CareerPathwayPage';
import BenchmarkLibrary from './pages/BenchmarkLibrary';
import DevelopmentReports from './pages/DevelopmentReports';
import DevelopmentReportBuilder from './pages/DevelopmentReportBuilder';
import DevelopmentReportView from './pages/DevelopmentReportView';
import DevelopmentReportPreview from './pages/DevelopmentReportPreview';
import DevelopmentReportPrint from './pages/DevelopmentReportPrint';
import SharedDevelopmentReport from './pages/SharedDevelopmentReport';
import AuthenticatedReportAsset from './pages/AuthenticatedReportAsset';
import AdminDevelopmentTags from './pages/AdminDevelopmentTags';
import MatchReportsPage from './pages/MatchReportsPage';
import MatchReportsEntry from './pages/MatchReportsEntry';
import MatchReportDetailPage from './pages/MatchReportDetailPage';
import InjuryRecoveryPage from './pages/InjuryRecoveryPage';
import ParentDashboard from './pages/ParentDashboard';
import AthleteFlags from './pages/AthleteFlags';
import SeasonReportBuilder from './pages/SeasonReportBuilder';
import SeasonReportPrint from './pages/SeasonReportPrint';
import SeasonReportView from './pages/SeasonReportView';
import TrialReportsList from './pages/TrialReportsList';
import TrialReportEditor from './pages/TrialReportEditor';
import AthleteManagement from './pages/AthleteManagement';
import AlertConfig from './pages/AlertConfig';
import SeasonReportCompare from './pages/SeasonReportCompare';
import SystemLogs from './pages/SystemLogs';
import AthleteModuleWrapper from './components/AthleteModuleWrapper';
import ChangePassword from './pages/ChangePassword';
import StaffCredentials from './pages/StaffCredentials';
import ManageAccounts from './pages/ManageAccounts';
import BulkImport from './pages/BulkImport';
import DataImport from './pages/DataImport';
import WellnessMonitoring from './pages/WellnessMonitoring';
import MentalPerformanceHub from './pages/MentalPerformanceHub';
import PerformancePrograms from './pages/PerformancePrograms';
import StaffWorkCenter from './pages/StaffWorkCenter';
import AthleteBiometrics from './pages/AthleteBiometrics';
import MediaLanding from './pages/MediaLanding';
import TagManagement from './pages/TagManagement';
import TeamState from './pages/TeamState';
import SquadAnalytics from './pages/SquadAnalytics';
import AcademyOverview from './pages/AcademyOverview';
import Seasons from './pages/Seasons';
import SeasonTransitionWizard from './pages/SeasonTransitionWizard';
import PFAPAIAssistant from './pages/PFAPAIAssistant';
import { GuideModeProvider } from './contexts/GuideModeContext';

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <GuideModeProvider>
          <AthleteProvider>
          <Routes>
            <Route path="/share/development-report/:capability" element={<SharedDevelopmentReport />} />
            <Route path="/share/development-report/:capability/print" element={<SharedDevelopmentReport printMode />} />
            <Route path="/development-report-assets/open" element={<ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}><AuthenticatedReportAsset /></ProtectedRoute>} />
            <Route path="/protected-files/open" element={<ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}><AuthenticatedReportAsset /></ProtectedRoute>} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            {/* Phase 8 — Staff Work Center replaces Focus Hub. Legacy
                /focus paths are kept as redirects so deep links and saved
                bookmarks continue to work. */}
            <Route
              path="/work-center"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <StaffWorkCenter />
                </ProtectedRoute>
              }
            />
            <Route path="/focus" element={<Navigate to="/work-center" replace />} />
            <Route path="/focus-hub" element={<Navigate to="/work-center" replace />} />
            <Route path="/focus-v2" element={<Navigate to="/work-center" replace />} />
            <Route path="/focus-legacy" element={<Navigate to="/work-center" replace />} />
            <Route
              path="/parent-dashboard"
              element={
                <ProtectedRoute allowedRoles={["Parent"]}>
                  <ParentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <AdminAthletes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-athletes"
              element={
                <ProtectedRoute allowedRoles={["Staff"]}>
                  <StaffAthletes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/visual-roster"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <VisualRoster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-profile"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <PlayerProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]} module="training" requiredLevel="view">
                  <StaffTraining />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-training"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]} module="training" requiredLevel="view">
                  <PlayerTraining />
                </ProtectedRoute>
              }
            />
            <Route
              path="/matches"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <MatchesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/matches/legacy"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <StaffMatches />
                </ProtectedRoute>
              }
            />
            <Route
              path="/matches/:matchId"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <MatchDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/matches/:matchId/performance"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <MatchPerformancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clubs"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <ClubsAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-performance"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <PlayerPerformance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/medical"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <StaffMedical />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-medical"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <PlayerMedical />
                </ProtectedRoute>
              }
            />
            <Route
              path="/academic"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <StaffAcademic />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-academic"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <PlayerAcademic />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <StaffFinance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/reports"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <FinanceReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-finance"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <PlayerFinance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-finance/reports"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <FinanceReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <StaffDocuments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-documents"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <PlayerDocuments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/announcements"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <StaffAnnouncements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-announcements"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <PlayerAnnouncements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/:athleteId/360"
              element={
                <ProtectedRoute>
                  <Athlete360Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-360"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <My360Redirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/compare"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <CompareAthletes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete-tags"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <TagManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-state"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <TeamState />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/squad"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <SquadAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/academy"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <AcademyOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seasons"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]} module="seasons" requiredLevel="view">
                  <Seasons />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seasons/transition"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <SeasonTransitionWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/intelligence/ai-assistant"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]} module="ai_assistant_view" requiredLevel="view">
                  <PFAPAIAssistant />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/manage"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <AthleteManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alert-config"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <AlertConfig />
                </ProtectedRoute>
              }
            />
            <Route
              path="/season-reports/compare"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <SeasonReportCompare />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <AdminUserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/intelligence"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <DecisionEnginePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assessments"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <StaffAssessments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assessments/dashboard"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <FitnessDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assessments/standards"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <StandardsReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assessments/bulk"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <BulkAssessmentsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assessments/bulk/:id"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <BulkAssessmentDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-assessments"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <PlayerAssessments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wellness"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <StaffWellness />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wellness-monitoring"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <WellnessMonitoring />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mental-hub"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <MentalPerformanceHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/performance-programs"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <PerformancePrograms />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-wellness"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <PlayerWellness />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/media"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <AthleteMedia />
                </ProtectedRoute>
              }
            />
            <Route
              path="/media"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <MediaLanding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-media"
              element={
                <ProtectedRoute allowedRoles={["Player", "Parent"]}>
                  <PlayerMedia />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/access-control"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <AccessControlPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/development-tags"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <AdminDevelopmentTags />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pdp"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <PDPPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/career-pathway"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <CareerPathwayPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/benchmarks"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]} module="benchmarks" requiredLevel="view">
                  <BenchmarkLibrary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/development-reports"
              element={
                <ProtectedRoute module="development_reports" requiredLevel="view">
                  <DevelopmentReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/development-reports/:reportId/edit"
              element={
                <ProtectedRoute module="development_reports" requiredLevel="edit">
                  <DevelopmentReportBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/development-reports/:reportId/preview"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <DevelopmentReportPreview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/development-reports/:reportId/print"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <DevelopmentReportPrint />
                </ProtectedRoute>
              }
            />
            <Route
              path="/development-reports/:reportId"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <DevelopmentReportView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/match-reports"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <MatchReportsEntry />
                </ProtectedRoute>
              }
            />
            <Route
              path="/match-reports/:matchId"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <MatchReportDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/injury-recovery"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <InjuryRecoveryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/biometrics"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <AthleteBiometrics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts-flags"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <AthleteFlags />
                </ProtectedRoute>
              }
            />
            <Route
              path="/season-reports"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <SeasonReportBuilder />
                </ProtectedRoute>
              }
            />
            <Route path="/season-report/print" element={<SeasonReportPrint />} />
            {/* Phase 76.3 — permanent saved-report route. Server-side RBAC
                gates Player/Parent to finalised reports only. */}
            <Route
              path="/season-reports/saved/:report_id"
              element={
                <ProtectedRoute>
                  <SeasonReportView />
                </ProtectedRoute>
              }
            />
            {/* Phase 83 — Trial Reports (independent from Season Reports).
                The list page is gated to Admin/Staff for the New-button;
                Player/Parent see only finalised reports (server-side). */}
            <Route
              path="/trial-reports"
              element={
                <ProtectedRoute module="trial_reports" requiredLevel="view">
                  <TrialReportsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trial-reports/:reportId"
              element={
                <ProtectedRoute module="trial_reports" requiredLevel="view">
                  <TrialReportEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/system-logs"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <SystemLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/staff-credentials"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <StaffCredentials />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/manage-accounts"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <ManageAccounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bulk-import"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <BulkImport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/data-import"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <DataImport />
                </ProtectedRoute>
              }
            />
            {/* Athlete-scoped module routes */}
            <Route
              path="/athletes/:athleteId/academic"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <AthleteModuleWrapper moduleName="Academic">
                    {({ athleteId }) => <StaffAcademic athleteId={athleteId} />}
                  </AthleteModuleWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/training"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]} module="training" requiredLevel="view">
                  <AthleteModuleWrapper moduleName="Training">
                    {({ athleteId }) => <StaffTraining athleteId={athleteId} />}
                  </AthleteModuleWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/health"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <AthleteModuleWrapper moduleName="Health">
                    {({ athleteId }) => <StaffMedical athleteId={athleteId} />}
                  </AthleteModuleWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/performance"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <AthleteModuleWrapper moduleName="Match Reports">
                    {({ athleteId }) => <MatchReportsPage athleteId={athleteId} />}
                  </AthleteModuleWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/pdp"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <AthleteModuleWrapper moduleName="Development Plan">
                    {({ athleteId }) => <PDPPage athleteId={athleteId} />}
                  </AthleteModuleWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/injury"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff", "Player", "Parent"]}>
                  <AthleteModuleWrapper moduleName="Injury & Recovery">
                    {({ athleteId }) => <InjuryRecoveryPage athleteId={athleteId} />}
                  </AthleteModuleWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/finance"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <AthleteModuleWrapper moduleName="Finance">
                    {({ athleteId }) => <StaffFinance athleteId={athleteId} />}
                  </AthleteModuleWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athletes/:athleteId/documents"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Staff"]}>
                  <AthleteModuleWrapper moduleName="Documents">
                    {({ athleteId }) => <StaffDocuments athleteId={athleteId} />}
                  </AthleteModuleWrapper>
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors />
          </AthleteProvider>
          </GuideModeProvider>
        </AuthProvider>
      </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
